/* global BigInt */
export const isLockedObservationStatus = (status) =>
  status === "APPROVED" || status === "REPORT_ISSUED";

export const isEgfrObservation = (row) => /\begfr\b/i.test(row.observation_name || "");
export const isCalculatedObservation = (row) => Boolean(row.observation_formula) || isEgfrObservation(row);
export const formulaReferences = (formula) => [...new Set(
  [...String(formula || "").matchAll(/#?(\d+)@/g)].map((match) => Number(match[1]))
)];

export function isManualSelfConversion(row) {
  const refs = formulaReferences(row.observation_formula);
  return refs.length === 1 && refs[0] === Number(row.observation_id) &&
    (row.machine_reading == null || String(row.machine_reading).trim() === "");
}

export function calculationContext(rows, rootId, { ageYears = null, gender = "" } = {}) {
  const byId = new Map(rows.map(row => [Number(row.observation_id), row]));
  const inputs = {}, formulas = {}, overrides = {}, machines = {}, seen = new Set();
  function walk(id) {
    if (seen.has(id)) return;
    seen.add(id);
    const row = byId.get(id);
    if (!row) { inputs[id] = null; return; }
    const override = row.calculation_override;
    if (id !== Number(rootId) && ["manual", "withhold"].includes(override?.mode)) {
      overrides[id] = { mode: override.mode, value: override.value || "" };
      return;
    }
    if (!isCalculatedObservation(row)) { inputs[id] = String(row.observation_value ?? ""); return; }
    formulas[id] = row.observation_formula || "eGFR:CKD-EPI-2021";
    const refs = isEgfrObservation(row) ? rows.filter(r => /\bcreatinine\b/i.test(r.observation_name || ""))
      .map(r => Number(r.observation_id)) : formulaReferences(row.observation_formula);
    refs.forEach(ref => {
      if (ref === id) machines[id] = String(row.machine_reading ?? "");
      else walk(ref);
    });
  }
  walk(Number(rootId));
  return { inputs, formulas, overrides, machines, ageYears, gender: gender || "" };
}

export function sameCalculationContext(a, b) {
  const sorted = value => value && typeof value === "object" && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).sort().map(key => [key, sorted(value[key])])) : value;
  return JSON.stringify(sorted(a)) === JSON.stringify(sorted(b));
}

function numeric(value) {
  if (value == null || typeof value === "boolean" || String(value).trim() === "") {
    throw new Error("Missing numeric input");
  }
  // Do not interpret hex, inequalities, commas or partial readings as numbers.
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(value).trim())) {
    throw new Error("Invalid numeric input");
  }
  const result = Number(value);
  if (!Number.isFinite(result)) throw new Error("Non-finite calculation");
  return result;
}

// Match Python round(float, 2), including binary halfway cases (e.g. 2.625).
export function roundResult(value) {
  numeric(value);
  const bytes = new DataView(new ArrayBuffer(8));
  bytes.setFloat64(0, Math.abs(value));
  const bits = bytes.getBigUint64(0);
  const exponent = Number((bits >> 52n) & 2047n);
  let numerator = (bits & ((1n << 52n) - 1n)) + (exponent ? 1n << 52n : 0n);
  const shift = (exponent || 1) - 1023 - 52;
  numerator *= 100n;
  let denominator = 1n;
  if (shift >= 0) numerator <<= BigInt(shift);
  else denominator <<= BigInt(-shift);
  let rounded = numerator / denominator;
  const remainder = numerator % denominator;
  if (remainder * 2n > denominator || (remainder * 2n === denominator && rounded % 2n)) rounded++;
  return numeric(Number(rounded) / 100 * (value < 0 ? -1 : 1));
}

// A small arithmetic parser, not eval/Function: catalogue text cannot execute JS.
export function evaluateFormula(formula, reference, ageYears) {
  if (typeof formula !== "string" || formula.length > 4096) throw new Error("Invalid formula");
  const tokens = [];
  let remaining = formula.trim();
  while (remaining) {
    const match = remaining.match(/^(#?\d+@|(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?|Math\.[A-Za-z]+|_AGE|\*\*|[+\-*/%(),])/);
    if (!match || tokens.length >= 256) throw new Error("Unsupported formula syntax");
    tokens.push(match[0]);
    remaining = remaining.slice(match[0].length).trimStart();
  }
  let position = 0;
  const take = (token) => tokens[position] === token && (++position > 0);
  const expect = (token) => { if (!take(token)) throw new Error(`Expected ${token}`); };
  const power = (a, b) => {
    if (Math.abs(b) > 100) throw new Error("Exponent outside supported limits");
    return numeric(a ** b);
  };
  function primary() {
    const token = tokens[position++];
    if (!token) throw new Error("Incomplete formula");
    if (token === "(") { const value = sum(); expect(")"); return value; }
    if (/^#?\d+@$/.test(token)) return numeric(reference(Number(token.replace(/[#@]/g, ""))));
    if (token === "_AGE") return numeric(ageYears);
    if (token.startsWith("Math.")) {
      const name = token.slice(5);
      expect("(");
      const args = [sum()];
      while (take(",")) args.push(sum());
      expect(")");
      const functions = { pow: power, min: Math.min, max: Math.max, abs: Math.abs,
        sqrt: Math.sqrt, round: (value) => Math.floor(value + 0.5), floor: Math.floor, ceil: Math.ceil };
      if (!Object.prototype.hasOwnProperty.call(functions, name) ||
          (name === "pow" ? args.length !== 2 :
            ["min", "max"].includes(name) ? args.length < 2 : args.length !== 1)) {
        throw new Error("Unsupported formula function");
      }
      return numeric(functions[name](...args));
    }
    return numeric(token);
  }
  function unary() {
    if (take("+")) return unary();
    if (take("-")) return -unary();
    const value = primary();
    return take("**") ? power(value, unary()) : value;
  }
  function product() {
    let value = unary();
    while (["*", "/", "%"].includes(tokens[position])) {
      const op = tokens[position++], right = unary();
      value = numeric(op === "*" ? value * right : op === "/" ? value / right : value - Math.floor(value / right) * right);
    }
    return value;
  }
  function sum() {
    let value = product();
    while (["+", "-"].includes(tokens[position])) {
      const op = tokens[position++], right = product();
      value = numeric(op === "+" ? value + right : value - right);
    }
    return value;
  }
  const value = sum();
  if (position !== tokens.length) throw new Error("Unsupported formula syntax");
  return numeric(value);
}

export function calculateInvestigation(rows, { ageYears, gender } = {}) {
  const byId = new Map(rows.map((row) => [Number(row.observation_id), row]));
  const values = new Map(), errors = new Map(), visiting = new Set();
  const inputIds = new Set(rows.flatMap((row) => formulaReferences(row.observation_formula)));
  if (rows.some(isEgfrObservation)) rows.filter((row) => /\bcreatinine\b/i.test(row.observation_name || ""))
    .forEach((row) => inputIds.add(Number(row.observation_id)));
  if (byId.size !== rows.length) {
    return { rows, errors: new Map([[null, "Duplicate observation IDs; reload and review this investigation"]]) };
  }
  function resolve(id) {
    if (errors.has(id)) throw new Error(errors.get(id));
    if (values.has(id)) return values.get(id);
    const row = byId.get(id);
    if (!row) throw new Error(`Missing observation ${id} in this investigation`);
    if (isLockedObservationStatus(row.status) || !isCalculatedObservation(row)) return numeric(row.observation_value);
    if (visiting.has(id)) throw new Error("Circular formula dependency");
    visiting.add(id);
    try {
      let result;
      const override = row.calculation_override;
      if (["manual", "withhold"].includes(override?.mode)) {
        if (!sameCalculationContext(override.context, calculationContext(rows, id, { ageYears: ageYears ?? null, gender }))) {
          throw new Error("Inputs changed; pathologist must review override");
        }
        if (override.mode === "withhold") {
          values.set(id, "");
          return "";
        }
        result = numeric(override.value);
        if (result < 0) throw new Error("Override must be nonnegative");
        for (const ref of formulaReferences(row.observation_formula)) {
          if (ref !== id && byId.get(ref)?.calculation_override?.mode === "manual") resolve(ref);
        }
        result = roundResult(result);
      } else if (isEgfrObservation(row)) {
        const sex = String(gender || "").toLowerCase();
        if (!["male", "m", "female", "f"].includes(sex) || numeric(ageYears) <= 0 || ageYears > 120) {
          throw new Error("Verify age and gender for eGFR");
        }
        const creatinine = rows.filter((r) => /\bcreatinine\b/i.test(r.observation_name || ""));
        if (creatinine.length !== 1) throw new Error("A single creatinine input is required in this investigation");
        const scr = resolve(Number(creatinine[0].observation_id));
        if (scr <= 0) throw new Error("Creatinine must be positive");
        // Preserve the existing CKD-EPI 2021 client formula and whole-number output.
        const female = sex === "female" || sex === "f";
        const ratio = scr / (female ? 0.7 : 0.9);
        result = Math.round(142 * Math.min(ratio, 1) ** (female ? -0.241 : -0.302) *
          Math.max(ratio, 1) ** -1.2 * 0.9938 ** ageYears * (female ? 1.012 : 1));
      } else {
        const references = formulaReferences(row.observation_formula);
        const self = references.includes(id);
        const raw = row.machine_reading;
        if (self && (raw == null || String(raw).trim() === "")) {
          if (references.length !== 1) throw new Error("Self-reference requires raw machine input");
          result = numeric(row.observation_value); // Manual value is already in reporting units.
        } else {
          result = evaluateFormula(row.observation_formula,
            (ref) => ref === id ? numeric(raw) : resolve(ref), ageYears);
        }
        result = roundResult(result);
      }
      values.set(id, numeric(result));
      return result;
    } catch (error) {
      errors.set(id, error.message);
      throw error;
    } finally { visiting.delete(id); }
  }
  const calculated = rows.map((row) => {
    if (isLockedObservationStatus(row.status) || !isCalculatedObservation(row)) {
      return { ...row, calculation_input: inputIds.has(Number(row.observation_id)) };
    }
    let value = "";
    try {
      const result = resolve(Number(row.observation_id));
      value = result === "" ? "" : isEgfrObservation(row) ? String(result) : result.toFixed(2);
    } catch (_) { /* Unresolved calculations remain blank, never a stale saved value. */ }
    return { ...row, observation_value: value,
      calculation_input_value: isManualSelfConversion(row) ? row.observation_value ?? "" : undefined,
      calculation_error: errors.get(Number(row.observation_id)) || null };
  });
  return { rows: calculated, errors };
}

export function calculateResultTree(tree, options) {
  const result = {}, issues = [];
  for (const [department, investigations] of Object.entries(tree || {})) {
    result[department] = {};
    for (const [investigation, wrapper] of Object.entries(investigations || {})) {
      const { rows, errors } = calculateInvestigation(wrapper.observations || [], options);
      result[department][investigation] = { ...wrapper, observations: rows };
      for (const [observationId, message] of errors) {
        issues.push({ department, investigation, observationId, message });
      }
    }
  }
  return { tree: result, issues };
}
